import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Home, 
  Building, 
  Building2, 
  Store, 
  ArrowRight,
  ArrowLeft,
  Check,
  X,
  MapPin,
  Plus,
  Shield,
  Clock,
  Star,
  Zap,
  Award,
  Calendar,
  DollarSign
} from 'lucide-react';
import { toast } from 'react-hot-toast';

const PropertySubscriptionFlow = ({ properties = [], plans = [], loading = false }) => {
  const [step, setStep] = useState(1);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [selectedBillingCycle, setSelectedBillingCycle] = useState('MONTHLY');
  const [subscribing, setSubscribing] = useState(false);

  // Debug: Log properties to console
  console.log('PropertySubscriptionFlow - Properties received:', properties);
  console.log('PropertySubscriptionFlow - Properties type:', typeof properties);
  console.log('PropertySubscriptionFlow - Properties is array:', Array.isArray(properties));
  console.log('PropertySubscriptionFlow - Properties length:', properties?.length);

  // Ensure properties is always an array
  const safeProperties = Array.isArray(properties) ? properties : [];

  const propertyIcons = {
    'Apartment': Building,
    'Condo': Building2,
    'HDB': Home,
    'Landed': Home,
    'Commercial': Store
  };

  const propertyTypeColors = {
    'Apartment': 'bg-blue-100 text-blue-800 border-blue-200',
    'Condo': 'bg-purple-100 text-purple-800 border-purple-200',
    'HDB': 'bg-green-100 text-green-800 border-green-200',
    'Landed': 'bg-yellow-100 text-yellow-800 border-yellow-200',
    'Commercial': 'bg-red-100 text-red-800 border-red-200'
  };

  const getPrice = (plan) => {
    return selectedBillingCycle === 'YEARLY' ? plan.yearlyPrice : plan.monthlyPrice;
  };

  const handleSubscribe = async () => {
    setSubscribing(true);
    try {
      // Prepare subscription data
      const subscriptionData = {
        propertyId: selectedProperty._id,
        propertyType: selectedProperty.type,
        billingCycle: selectedBillingCycle,
        planData: {
          monthlyPrice: selectedPlan.monthlyPrice,
          yearlyPrice: selectedPlan.yearlyPrice || selectedPlan.monthlyPrice * 10, // Default yearly pricing
          name: selectedPlan.name,
          features: selectedPlan.features
        },
        paymentMethod: {
          type: 'HITPAY'
        }
      };

      console.log('Creating subscription with data:', subscriptionData);

      // Call the subscription API
      const response = await fetch('/api/subscriptions/subscribe', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(subscriptionData)
      });

      const result = await response.json();
      console.log('Subscription API response:', result);

      if (response.ok && result.success) {
        if (result.paymentUrl) {
          // Redirect to HitPay payment page
          toast.success('Redirecting to payment page...', { duration: 2000 });
          setTimeout(() => {
            window.location.href = result.paymentUrl;
          }, 1000);
        } else if (result.subscription) {
          // Subscription created successfully (maybe for demo/free)
          toast.success('Subscription activated successfully!');
          setTimeout(() => {
            window.location.href = '/dashboard';
          }, 2000);
        } else {
          throw new Error('No payment URL or subscription returned');
        }
      } else {
        throw new Error(result.message || 'Failed to create subscription');
      }
    } catch (error) {
      console.error('Subscription error:', error);
      toast.error(error.message || 'Subscription failed. Please try again.');
    } finally {
      setSubscribing(false);
    }
  };

  const PropertySelector = () => {
    return (
      <div className="space-y-8">
        {/* Add Property Button - Top Left */}
        <div className="flex justify-between items-center mb-8">
          <button
            onClick={() => window.location.href = '/properties'}
            className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-xl hover:from-orange-600 hover:to-orange-700 transition-all duration-300 font-semibold shadow-lg hover:shadow-xl"
          >
            <Plus className="h-5 w-5 mr-2" />
            Add Property
          </button>
        </div>

        <div className="text-center mb-12">
          <div className="inline-block px-4 py-2 bg-orange-100 rounded-full text-orange-600 text-sm font-semibold mb-4">
            Step 1 of 2
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Select a Property</h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto mb-4">Choose which property you want to subscribe to a maintenance plan</p>
        </div>

        {safeProperties.length === 0 ? (
          <div className="max-w-md mx-auto">
            <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-12 text-center">
              <div className="w-20 h-20 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Home className="h-10 w-10 text-orange-600" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">No Properties Found</h3>
              <p className="text-gray-600 mb-8 leading-relaxed">You need to add a property to subscribe to a maintenance plan.</p>
              <button
                onClick={() => window.location.href = '/properties'}
                className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-xl hover:from-orange-600 hover:to-orange-700 transition-all duration-300 font-semibold shadow-lg hover:shadow-xl"
              >
                <Plus className="h-5 w-5 mr-2" />
                Add Property
              </button>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {safeProperties.map((property) => {
              const IconComponent = propertyIcons[property.type] || Home;
              const isSelected = selectedProperty?._id === property._id;
              const hasActiveSubscription = property.hasActiveSubscription ?? (
                property.subscription && ((property.subscription.status === 'ACTIVE') || property.subscription.isActive)
              );

              return (
                <motion.div
                  key={property._id}
                  whileHover={{ scale: hasActiveSubscription ? 1 : 1.02 }}
                  whileTap={{ scale: hasActiveSubscription ? 1 : 0.98 }}
                  onClick={() => {
                    if (hasActiveSubscription) {
                      toast.error('This property already has an active subscription', { duration: 3000, icon: '🔒' });
                    } else {
                      setSelectedProperty(property);
                    }
                  }}
                  className={`relative bg-white rounded-2xl shadow-lg border-2 transition-all duration-300 overflow-hidden ${
                    isSelected && !hasActiveSubscription
                      ? 'border-orange-500 shadow-orange-200 shadow-2xl ring-4 ring-orange-100 cursor-pointer'
                      : hasActiveSubscription
                      ? 'border-gray-300 opacity-75 cursor-not-allowed'
                      : 'border-gray-200 hover:border-orange-300 hover:shadow-xl cursor-pointer'
                  }`}
                >
                  <div className="p-6">
                    <div className="flex items-center justify-between mb-4">
                      <div className={`flex items-center p-3 rounded-xl ${hasActiveSubscription ? 'bg-gray-100' : 'bg-orange-50'}`}>
                        <IconComponent className={`h-6 w-6 ${hasActiveSubscription ? 'text-gray-400' : 'text-orange-600'}`} />
                      </div>
                      <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold border ${hasActiveSubscription ? 'bg-gray-100 text-gray-600 border-gray-200' : propertyTypeColors[property.type] || 'bg-gray-100 text-gray-800 border-gray-200'}`}>
                        {property.type}
                      </span>
                    </div>

                    <h3 className={`text-lg font-bold mb-2 ${hasActiveSubscription ? 'text-gray-500' : 'text-gray-900'}`}>
                      {property.name}
                      <span className={`block text-sm font-normal ${hasActiveSubscription ? 'text-gray-400' : 'text-orange-600'}`}>
                        {property.address?.unit && `Unit ${property.address.unit}`}
                      </span>
                    </h3>

                    {/* Enhanced Address Display */}
                    <div className="space-y-2 mb-4">
                      <div className="flex items-start">
                        <MapPin className={`h-4 w-4 mt-0.5 mr-2 flex-shrink-0 ${hasActiveSubscription ? 'text-gray-400' : 'text-gray-500'}`} />
                        <div className={`text-sm ${hasActiveSubscription ? 'text-gray-500' : 'text-gray-700'}`}>
                          <div className="font-medium">{property.address?.street}</div>
                          {property.address?.unit && (
                            <div className="text-gray-500">Unit {property.address.unit}</div>
                          )}
                          <div className="text-gray-500">
                            {property.address?.city || 'Singapore'} {property.address?.postalCode}
                          </div>
                        </div>
                      </div>

                      {/* Property Details */}
                      {(property.details?.size || property.details?.bedrooms || property.details?.bathrooms || property.details?.rooms) && (
                        <div className={`text-xs ${hasActiveSubscription ? 'text-gray-400' : 'text-gray-600'} flex flex-wrap gap-2 mt-2`}>
                          {property.details?.size && (
                            <span className="bg-gray-100 px-2 py-1 rounded">
                              {property.details.size} sq ft
                            </span>
                          )}
                          {property.details?.rooms && (
                            <span className="bg-gray-100 px-2 py-1 rounded">
                              {property.details.rooms} rooms
                            </span>
                          )}
                          {property.details?.bedrooms && (
                            <span className="bg-gray-100 px-2 py-1 rounded">
                              {property.details.bedrooms} bed
                            </span>
                          )}
                          {property.details?.bathrooms && (
                            <span className="bg-gray-100 px-2 py-1 rounded">
                              {property.details.bathrooms} bath
                            </span>
                          )}
                          {property.details?.floorNumber && (
                            <span className="bg-gray-100 px-2 py-1 rounded">
                              Floor {property.details.floorNumber}
                            </span>
                          )}
                        </div>
                      )}
                    </div>

                    {hasActiveSubscription && (
                      <div className="bg-green-50 border border-green-200 rounded-lg p-3 mt-4">
                        <p className="text-xs text-green-800 font-medium flex items-center gap-1.5">
                          <Check className="h-4 w-4" />
                          Active Subscription
                        </p>
                      </div>
                    )}

                    {!hasActiveSubscription && (
                      <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 mt-4">
                        <p className="text-xs text-orange-800 font-medium flex items-center gap-1.5">
                          <X className="h-4 w-4" />
                          No Active Subscription
                        </p>
                      </div>
                    )}

                    {isSelected && !hasActiveSubscription && (
                      <div className="absolute top-4 right-4 bg-orange-600 text-white rounded-full p-2 shadow-lg">
                        <Check className="h-5 w-5" />
                      </div>
                    )}

                    {hasActiveSubscription && (
                      <div className="absolute top-4 right-4 bg-gray-400 text-white rounded-full p-2 shadow-lg">
                        <X className="h-5 w-5" />
                      </div>
                    )}
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}

        {selectedProperty && !(selectedProperty.hasActiveSubscription ?? (selectedProperty.subscription && ((selectedProperty.subscription.status === 'ACTIVE') || selectedProperty.subscription.isActive))) && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex justify-center mt-12"
          >
            <button
              onClick={() => setStep(2)}
              className="flex items-center px-8 py-4 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-xl hover:from-orange-600 hover:to-orange-700 transition-all duration-300 font-semibold text-lg shadow-lg hover:shadow-xl"
            >
              Continue to Plans
              <ArrowRight className="h-6 w-6 ml-2" />
            </button>
          </motion.div>
        )}
      </div>
    );
  };

  const PlanSelector = () => {
    // Filter plans based on selected property type
    const availablePlans = plans.filter(plan => plan.propertyType === selectedProperty.type);

    return (
      <div className="space-y-8">
        <div className="text-center mb-12">
          <div className="inline-block px-4 py-2 bg-orange-100 rounded-full text-orange-600 text-sm font-semibold mb-4">
            Step 2 of 2
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Choose Your Plan</h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto mb-6">
            Select a maintenance plan for <span className="font-semibold text-orange-600">{selectedProperty.name}</span>
          </p>
          
          {/* Selected Property Summary */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 max-w-md mx-auto">
            <div className="flex items-center justify-center mb-2">
              <MapPin className="h-4 w-4 text-gray-500 mr-2" />
              <span className="text-sm font-medium text-gray-900">{selectedProperty.address?.street}</span>
            </div>
            {selectedProperty.address?.unit && (
              <div className="text-xs text-gray-600 mb-1">Unit {selectedProperty.address.unit}</div>
            )}
            <div className="text-xs text-gray-600">
              {selectedProperty.address?.city || 'Singapore'} {selectedProperty.address?.postalCode}
            </div>
            {(selectedProperty.details?.size || selectedProperty.details?.rooms) && (
              <div className="flex justify-center gap-2 mt-3">
                {selectedProperty.details?.size && (
                  <span className="bg-orange-50 text-orange-700 px-2 py-1 rounded text-xs">
                    {selectedProperty.details.size} sq ft
                  </span>
                )}
                {selectedProperty.details?.rooms && (
                  <span className="bg-orange-50 text-orange-700 px-2 py-1 rounded text-xs">
                    {selectedProperty.details.rooms} rooms
                  </span>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Enhanced Billing Cycle Toggle */}
        <div className="flex justify-center mb-12">
          <div className="bg-white border-2 border-gray-200 p-2 rounded-xl shadow-sm">
            <div className="flex items-center gap-1">
              <button
                onClick={() => setSelectedBillingCycle('MONTHLY')}
                className={`px-8 py-4 rounded-lg transition-all font-semibold text-sm relative ${
                  selectedBillingCycle === 'MONTHLY'
                    ? 'bg-gradient-to-r from-orange-500 to-orange-600 text-white shadow-lg'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                }`}
              >
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  <span>Monthly</span>
                </div>
                <div className={`text-xs mt-1 ${selectedBillingCycle === 'MONTHLY' ? 'text-orange-100' : 'text-gray-500'}`}>
                  Pay monthly
                </div>
              </button>
              
              <button
                onClick={() => setSelectedBillingCycle('YEARLY')}
                className={`px-8 py-4 rounded-lg transition-all font-semibold text-sm relative ${
                  selectedBillingCycle === 'YEARLY'
                    ? 'bg-gradient-to-r from-orange-500 to-orange-600 text-white shadow-lg'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                }`}
              >
                <div className="flex items-center gap-2">
                  <DollarSign className="h-4 w-4" />
                  <span>Yearly</span>
                  <span className="bg-green-100 text-green-800 px-2 py-0.5 rounded-full text-xs font-bold">
                    Save 17%
                  </span>
                </div>
                <div className={`text-xs mt-1 ${selectedBillingCycle === 'YEARLY' ? 'text-orange-100' : 'text-gray-500'}`}>
                  2 months free
                </div>
              </button>
            </div>
          </div>
        </div>

        {availablePlans.length === 0 ? (
          <div className="max-w-md mx-auto">
            <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-12 text-center">
              <div className="w-20 h-20 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <X className="h-10 w-10 text-orange-600" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">No Plans Available</h3>
              <p className="text-gray-600 leading-relaxed">
                No maintenance plans are available for this property type yet.
              </p>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-8">
            {availablePlans.map((plan) => {
              const isSelected = selectedPlan?._id === plan._id;
              const price = getPrice(plan);
              const monthlyPrice = plan.monthlyPrice;
              const yearlyPrice = plan.yearlyPrice || monthlyPrice * 10;
              const monthlySavings = selectedBillingCycle === 'YEARLY' ? (monthlyPrice * 12 - yearlyPrice) : 0;
              const monthlyEquivalent = selectedBillingCycle === 'YEARLY' ? (yearlyPrice / 12) : monthlyPrice;

              return (
                <motion.div
                  key={plan._id}
                  whileHover={{ scale: 1.02, y: -4 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => setSelectedPlan(plan)}
                  className={`relative bg-white rounded-2xl shadow-lg border-2 transition-all duration-300 cursor-pointer overflow-hidden ${
                    isSelected
                      ? 'border-orange-500 shadow-orange-200 shadow-2xl ring-4 ring-orange-100'
                      : 'border-gray-200 hover:border-orange-300 hover:shadow-xl'
                  }`}
                >
                  {/* Plan Header */}
                  <div className={`p-6 text-center ${isSelected ? 'bg-gradient-to-r from-orange-50 to-orange-100' : 'bg-gray-50'}`}>
                    <div className="flex justify-center mb-3">
                      <div className={`p-3 rounded-full ${isSelected ? 'bg-orange-200' : 'bg-orange-100'}`}>
                        <Shield className={`h-6 w-6 ${isSelected ? 'text-orange-700' : 'text-orange-600'}`} />
                      </div>
                    </div>
                    
                    <h3 className="text-xl font-bold text-gray-900 mb-2">{plan.name}</h3>
                    
                    {/* Pricing Section */}
                    <div className="mb-4">
                      <motion.div 
                        key={selectedBillingCycle}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.3 }}
                        className="flex items-baseline justify-center mb-2"
                      >
                        <span className="text-4xl font-bold text-orange-600">${price}</span>
                        <span className="text-lg text-gray-600 ml-2">
                          /{selectedBillingCycle === 'YEARLY' ? 'year' : 'month'}
                        </span>
                      </motion.div>
                      
                      {selectedBillingCycle === 'YEARLY' && (
                        <motion.div 
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.3, delay: 0.1 }}
                          className="space-y-1"
                        >
                          <div className="text-sm text-gray-600">
                            ${monthlyEquivalent.toFixed(0)}/month when paid yearly
                          </div>
                          {monthlySavings > 0 && (
                            <div className="inline-flex items-center px-3 py-1 bg-green-100 text-green-800 rounded-full text-xs font-semibold">
                              <Star className="h-3 w-3 mr-1" />
                              Save ${monthlySavings} per year
                            </div>
                          )}
                        </motion.div>
                      )}
                      
                      {selectedBillingCycle === 'MONTHLY' && (
                        <motion.div 
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.3, delay: 0.1 }}
                          className="text-sm text-gray-600"
                        >
                          Billed monthly • Cancel anytime
                        </motion.div>
                      )}
                    </div>
                    
                    <p className="text-gray-600 text-sm leading-relaxed">{plan.description}</p>
                  </div>

                  {/* Plan Features */}
                  <div className="p-6">
                    <div className="space-y-4 mb-6">
                      <div className="flex items-center text-sm text-gray-700 font-medium mb-3">
                        <Award className="h-4 w-4 text-orange-600 mr-2" />
                        What's included:
                      </div>
                      
                      {plan.features?.map((feature, index) => (
                        <div key={index} className="flex items-start">
                          <Check className="h-4 w-4 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                          <span className="text-gray-700 text-sm leading-relaxed">{feature}</span>
                        </div>
                      ))}
                    </div>

                    {/* Additional Plan Details */}
                    <div className="border-t border-gray-200 pt-4">
                      <div className="grid grid-cols-2 gap-4 text-center">
                        <div>
                          <div className="flex items-center justify-center mb-1">
                            <Clock className="h-4 w-4 text-gray-500 mr-1" />
                          </div>
                          <div className="text-xs text-gray-500">Response Time</div>
                          <div className="text-sm font-medium text-gray-900">24-48 hours</div>
                        </div>
                        <div>
                          <div className="flex items-center justify-center mb-1">
                            <Zap className="h-4 w-4 text-gray-500 mr-1" />
                          </div>
                          <div className="text-xs text-gray-500">Inspections</div>
                          <div className="text-sm font-medium text-gray-900">Monthly</div>
                        </div>
                      </div>
                    </div>

                    {/* Selection Indicator */}
                    {isSelected && (
                      <div className="absolute top-4 right-4 bg-orange-600 text-white rounded-full p-2 shadow-lg">
                        <Check className="h-5 w-5" />
                      </div>
                    )}
                  </div>

                  {/* Recommended Badge */}
                  {plan.recommended && (
                    <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                      <span className="bg-gradient-to-r from-orange-500 to-orange-600 text-white px-4 py-1 rounded-full text-sm font-semibold shadow-lg flex items-center gap-1">
                        <Star className="h-3 w-3" />
                        Most Popular
                      </span>
                    </div>
                  )}
                </motion.div>
              );
            })}
          </div>
        )}

        {/* Selected Plan Details */}
        {selectedPlan && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-12"
          >
            <div className="bg-gradient-to-r from-orange-50 to-orange-100 rounded-2xl p-8 border border-orange-200">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">
                    Plan Summary: {selectedPlan.name}
                  </h3>
                  <p className="text-gray-600">Review your selection before proceeding</p>
                </div>
                <div className="text-right">
                  <div className="text-3xl font-bold text-orange-600">
                    ${getPrice(selectedPlan)}
                  </div>
                  <div className="text-gray-600">
                    /{selectedBillingCycle === 'YEARLY' ? 'year' : 'month'}
                  </div>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-8">
                {/* Plan Features */}
                <div>
                  <h4 className="font-semibold text-gray-900 mb-4 flex items-center">
                    <Award className="h-5 w-5 text-orange-600 mr-2" />
                    What's Included
                  </h4>
                  <div className="space-y-3">
                    {selectedPlan.features?.map((feature, index) => (
                      <div key={index} className="flex items-start">
                        <Check className="h-4 w-4 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-700 text-sm">{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Billing Details */}
                <div>
                  <h4 className="font-semibold text-gray-900 mb-4 flex items-center">
                    <DollarSign className="h-5 w-5 text-orange-600 mr-2" />
                    Billing Details
                  </h4>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Property Type:</span>
                      <span className="font-medium text-gray-900">{selectedProperty.type}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Billing Cycle:</span>
                      <span className="font-medium text-gray-900">{selectedBillingCycle}</span>
                    </div>
                    {selectedBillingCycle === 'YEARLY' && (
                      <>
                        <div className="flex justify-between items-center">
                          <span className="text-gray-600">Monthly Equivalent:</span>
                          <span className="font-medium text-gray-900">
                            ${((selectedPlan.yearlyPrice || selectedPlan.monthlyPrice * 10) / 12).toFixed(0)}/month
                          </span>
                        </div>
                        <div className="flex justify-between items-center text-green-600">
                          <span>Annual Savings:</span>
                          <span className="font-medium">
                            ${(selectedPlan.monthlyPrice * 12 - (selectedPlan.yearlyPrice || selectedPlan.monthlyPrice * 10))}
                          </span>
                        </div>
                      </>
                    )}
                    <div className="border-t border-orange-200 pt-3 mt-3">
                      <div className="flex justify-between items-center text-lg font-bold">
                        <span className="text-gray-900">Total:</span>
                        <span className="text-orange-600">${getPrice(selectedPlan)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Next Steps */}
              <div className="mt-8 p-6 bg-white rounded-xl border border-orange-200">
                <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                  <Zap className="h-5 w-5 text-orange-600 mr-2" />
                  What Happens Next
                </h4>
                <div className="grid md:grid-cols-3 gap-4 text-sm text-gray-600">
                  <div className="text-center">
                    <div className="w-8 h-8 bg-orange-100 text-orange-600 rounded-full flex items-center justify-center mx-auto mb-2 font-bold">1</div>
                    <div className="font-medium">Secure Payment</div>
                    <div>Complete payment via HitPay</div>
                  </div>
                  <div className="text-center">
                    <div className="w-8 h-8 bg-orange-100 text-orange-600 rounded-full flex items-center justify-center mx-auto mb-2 font-bold">2</div>
                    <div className="font-medium">Instant Activation</div>
                    <div>Subscription activates immediately</div>
                  </div>
                  <div className="text-center">
                    <div className="w-8 h-8 bg-orange-100 text-orange-600 rounded-full flex items-center justify-center mx-auto mb-2 font-bold">3</div>
                    <div className="font-medium">Schedule Inspection</div>
                    <div>Team will contact you within 24 hours</div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* Continue Button */}
        <div className="flex justify-center items-center gap-4 mt-12">
          <button
            onClick={() => setStep(1)}
            className="flex items-center px-6 py-3 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition-all duration-300 font-semibold"
          >
            <ArrowLeft className="h-5 w-5 mr-2" />
            Back to Properties
          </button>
          
          {selectedPlan && (
            <button
              onClick={handleSubscribe}
              disabled={subscribing}
              className="flex items-center px-8 py-4 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-xl hover:from-orange-600 hover:to-orange-700 transition-all duration-300 font-semibold text-lg shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {subscribing ? (
                <>
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white mr-2"></div>
                  Creating Subscription...
                </>
              ) : (
                <>
                  Subscribe & Pay Now
                  <ArrowRight className="h-6 w-6 ml-2" />
                </>
              )}
            </button>
          )}
        </div>
      </div>
    );
  };

  const StepIndicator = () => (
    <div className="mb-12">
      <div className="max-w-2xl mx-auto">
        <div className="flex items-center justify-between relative">
          {/* Step 1 */}
          <div className="flex flex-col items-center relative z-10 flex-1">
            <div className={`flex items-center justify-center w-12 h-12 rounded-full font-bold text-lg transition-all duration-300 ${
              step >= 1 ? 'bg-orange-600 text-white shadow-lg' : 'bg-gray-200 text-gray-500'
            }`}>
              {step > 1 ? <Check className="w-6 h-6" /> : '1'}
            </div>
            <div className={`mt-3 text-sm font-semibold transition-colors ${
              step >= 1 ? 'text-gray-900' : 'text-gray-500'
            }`}>
              Select Property
            </div>
          </div>

          {/* Connector Line */}
          <div className="flex-1 px-4 relative" style={{ top: '-20px' }}>
            <div className="h-1 bg-gray-200 rounded-full overflow-hidden">
              <div
                className={`h-full bg-orange-600 transition-all duration-500 ${
                  step >= 2 ? 'w-full' : 'w-0'
                }`}
              ></div>
            </div>
          </div>

          {/* Step 2 */}
          <div className="flex flex-col items-center relative z-10 flex-1">
            <div className={`flex items-center justify-center w-12 h-12 rounded-full font-bold text-lg transition-all duration-300 ${
              step >= 2 ? 'bg-orange-600 text-white shadow-lg' : 'bg-gray-200 text-gray-500'
            }`}>
              2
            </div>
            <div className={`mt-3 text-sm font-semibold transition-colors ${
              step >= 2 ? 'text-gray-900' : 'text-gray-500'
            }`}>
              Choose Plan
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Modern Hero Section - Matching HomePage Style */}
      <section className="relative overflow-hidden">
        {/* Background with geometric patterns */}
        <div className="absolute inset-0 bg-orange-600">
          <div className="absolute inset-0 bg-gradient-to-br from-orange-500 via-orange-600 to-orange-700"></div>
          {/* Geometric shapes */}
          <div className="absolute top-0 right-0 w-96 h-96 bg-orange-500 rounded-full opacity-20 transform translate-x-32 -translate-y-32"></div>
          <div className="absolute bottom-0 left-0 w-80 h-80 bg-orange-700 rounded-full opacity-30 transform -translate-x-24 translate-y-24"></div>
          <div className="absolute top-1/2 left-1/4 w-64 h-64 bg-white opacity-5 rounded-full"></div>
          <div className="absolute top-1/4 right-1/3 w-48 h-48 bg-orange-400 rounded-full opacity-10 transform rotate-45"></div>
        </div>

        <div className="relative container mx-auto px-4 py-24">
          <div className="max-w-4xl mx-auto text-center text-white">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6 }}
              className="mb-6"
            >
              <span className="inline-block px-4 py-2 bg-orange-500 bg-opacity-30 rounded-full text-orange-100 text-sm font-medium mb-4 backdrop-blur-sm">
                Subscription Plans
              </span>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.1 }}
              className="text-4xl md:text-6xl font-bold mb-6 leading-tight"
            >
              Subscribe to
              <span className="block text-orange-200">Property Maintenance</span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.6 }}
              className="text-xl text-orange-100 max-w-3xl mx-auto mb-10 leading-relaxed"
            >
              Professional property maintenance services tailored to your needs. Choose the perfect plan for your property.
            </motion.p>
          </div>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4">
          <StepIndicator />

          <AnimatePresence mode="wait">
            {step === 1 && (
              <motion.div
                key="property-selector"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                transition={{ duration: 0.3 }}
              >
                <PropertySelector />
              </motion.div>
            )}
            {step === 2 && (
              <motion.div
                key="plan-selector"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                transition={{ duration: 0.3 }}
              >
                <PlanSelector />
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </section>
    </div>
  );
};

export default PropertySubscriptionFlow;